
const easyToSell = [
  {
    icon: 'ri-calendar-check-line',
    title: '10 Years Old or Newer',
    description: 'The sweet spot. Buyers want modern safety features and tech. 2015 and up moves fast.',
  },
  {
    icon: 'ri-road-map-line',
    title: 'Under 120,000 Miles',
    description: 'Not a hard cutoff — but lower miles means more buyers, faster sale, better price.',
  },
  {
    icon: 'ri-tools-line',
    title: 'Clean Service History',
    description: 'Oil changes, timing belts, brakes — documented maintenance is worth real money to buyers.',
  },
  {
    icon: 'ri-shield-check-line',
    title: 'No Major Accident History',
    description: 'A clean Carfax isn\'t everything, but it removes the biggest buyer objection before it starts.',
  },
  {
    icon: 'ri-car-wash-line',
    title: 'Cosmetically Solid',
    description: 'Minor dings and wear are fine — we can recondition. Flood damage, frame damage, or major rust? That\'s a different conversation.',
  },
  {
    icon: 'ri-price-tag-3-line',
    title: 'Priced to Actually Sell',
    description: 'We\'ll run the comps together. If your expectations are realistic, we can move it. If not, we\'ll tell you honestly.',
  },
];

const hardToSell = [
  {
    icon: 'ri-alert-line',
    title: 'Salvage or Rebuilt Title',
    description: 'Financing is nearly impossible on these, which cuts your buyer pool by 80%. We\'ll be upfront if it\'s not a good fit.',
  },
  {
    icon: 'ri-tools-fill',
    title: 'Major Mechanical Issues',
    description: 'We can handle light reconditioning. But if it needs an engine or transmission, the math usually doesn\'t work in your favor.',
  },
  {
    icon: 'ri-drop-line',
    title: 'Flood or Fire Damage',
    description: 'No exceptions here. We won\'t sell a car we wouldn\'t buy ourselves.',
  },
  {
    icon: 'ri-time-line',
    title: 'Very High Mileage (200k+)',
    description: 'Not impossible — some buyers want a high-mile workhorse. But the audience is smaller and the price ceiling is lower.',
  },
];

export default function WhatWeLookFor() {
  return (
    <section className="py-20 bg-[#F7F5F0]">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-14">
          <span className="inline-block bg-[#E8B84B]/20 text-[#8a6a1a] text-sm font-semibold uppercase tracking-widest px-4 py-1.5 rounded-full mb-4">
            Straight Talk
          </span>
          <h2 className="font-['Oswald'] text-4xl md:text-5xl font-bold text-[#1C2B1E] mb-4">
            What We Actually Look For
          </h2>
          <div className="w-24 h-1 bg-[#E8B84B] mx-auto mb-6"></div>
          <p className="text-lg text-gray-700 max-w-2xl mx-auto">
            We won't waste your time — or ours. Here's an honest breakdown of what sells fast, what sells slow, and what we'll tell you upfront isn't a good fit.
          </p>
        </div>

        {/* Easy to Sell */}
        <div className="mb-12">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-8 h-8 flex items-center justify-center bg-[#1C2B1E] rounded-full">
              <i className="ri-thumb-up-line text-[#E8B84B] text-sm"></i>
            </div>
            <h3 className="font-['Oswald'] text-2xl font-bold text-[#1C2B1E]">
              Easy to Sell — These Move Fast
            </h3>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {easyToSell.map((item, i) => (
              <div
                key={i}
                className="bg-white rounded-xl p-6 border border-[#E8E2D6] hover:border-[#E8B84B]/50 hover:shadow-md transition-all duration-200"
              >
                <div className="w-10 h-10 flex items-center justify-center bg-[#E8B84B]/15 rounded-lg mb-4">
                  <i className={`${item.icon} text-xl text-[#8a6a1a]`}></i>
                </div>
                <h4 className="font-semibold text-[#1C2B1E] mb-2">{item.title}</h4>
                <p className="text-sm text-gray-600 leading-relaxed">{item.description}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Hard to Sell */}
        <div className="mb-12">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-8 h-8 flex items-center justify-center bg-red-100 rounded-full">
              <i className="ri-error-warning-line text-red-500 text-sm"></i>
            </div>
            <h3 className="font-['Oswald'] text-2xl font-bold text-[#1C2B1E]">
              Harder to Sell — We'll Be Honest With You
            </h3>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
            {hardToSell.map((item, i) => (
              <div
                key={i}
                className="bg-white rounded-xl p-6 border border-red-100 hover:border-red-200 hover:shadow-md transition-all duration-200"
              >
                <div className="w-10 h-10 flex items-center justify-center bg-red-50 rounded-lg mb-4">
                  <i className={`${item.icon} text-xl text-red-400`}></i>
                </div>
                <h4 className="font-semibold text-[#1C2B1E] mb-2">{item.title}</h4>
                <p className="text-sm text-gray-600 leading-relaxed">{item.description}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Honest callout */}
        <div className="bg-[#1C2B1E] rounded-2xl p-8 flex flex-col md:flex-row items-center gap-6">
          <div className="w-14 h-14 flex-shrink-0 flex items-center justify-center bg-[#E8B84B]/20 rounded-full">
            <i className="ri-chat-check-line text-2xl text-[#E8B84B]"></i>
          </div>
          <div className="flex-1 text-center md:text-left">
            <h4 className="font-['Oswald'] text-xl font-bold text-white mb-2">
              Not Sure If Your Car Qualifies? Just Ask.
            </h4>
            <p className="text-white/70 text-sm leading-relaxed">
              We'd rather spend 5 minutes on the phone telling you it's not a fit than waste both our time going through the motions. If it's a good candidate, we'll tell you exactly what to expect — timeline, price range, and our fee. No pressure, no pitch.
            </p>
          </div>
          <a
            href="/contact"
            className="flex-shrink-0 inline-flex items-center gap-2 bg-[#E8B84B] text-[#1C2B1E] px-6 py-3 rounded-full font-bold hover:bg-[#d4a43a] transition-all whitespace-nowrap text-sm"
          >
            Ask Us Directly
            <i className="ri-arrow-right-line"></i>
          </a>
        </div>
      </div>
    </section>
  );
}
